import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-markdown-preview/markdown.css';
import Navbar from '../Navbar/Navbar';
import Footer from '../Footer/Footer';
import { fetchNewsPostById, fetchNewsImages } from '../../lib/newsPostsApi';
import { fetchAttachments } from '../../lib/attachmentsApi';

import './Separate.scss';
import '../News/Admin.scss';

const Separate = () => {
    const { id } = useParams();

    const [newsItem, setNewsItem] = useState(null);
    const [gallery, setGallery] = useState([]);
    const [attachments, setAttachments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadArticle = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await fetchNewsPostById(id);
            if (data.status !== 'published') throw new Error('Article not found');
            setNewsItem(data);
            setGallery(await fetchNewsImages(id));
            setAttachments(await fetchAttachments({ newsPostId: id }));
        } catch (err) {
            setError(err.message || 'Article not found');
            setNewsItem(null);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        loadArticle();
    }, [loadArticle]);

    if (loading) {
        return (
            <div className="newsStory">
                <Navbar/>
                <p className="newsStatus">Loading...</p>
            </div>
        );
    }

    if (error || !newsItem) {
        return (
            <div className="newsStory">
                <Navbar/>
                <main className="newsStory__container">
                    <p className="newsStatus newsStatus--error">{error || 'Article not found'}</p>
                    <Link className="newsStory__back" to="/news">← Back to news</Link>
                </main>
                <Footer/>
            </div>
        );
    }

    return (
        <div className="newsStory">
            <Navbar/>
            <article className="newsStory__container">
                <Link className="newsStory__back" to="/news">← Back to news</Link>

                {newsItem.cover_image_url && (
                    <div className="newsStory__media">
                        <img src={newsItem.cover_image_url} alt={newsItem.title} />
                    </div>
                )}

                <header className="newsStory__header">
                    <h1>{newsItem.title}</h1>
                    <time className="newsStory__date" dateTime={newsItem.date}>
                        {new Date(newsItem.date).toLocaleDateString()}
                    </time>
                </header>

                <div className="newsStory__content" data-color-mode="light">
                    <MDEditor.Markdown source={newsItem.body || ''} />
                </div>

                {gallery.length > 0 && (
                    <section className="newsStory__gallery">
                        <h2>Gallery</h2>
                        <div className="newsStory__galleryGrid">
                            {gallery.map((img) => (
                                <img key={img.id} src={img.image_url} alt="" loading="lazy" />
                            ))}
                        </div>
                    </section>
                )}

                {attachments.length > 0 && (
                    <section className="newsStory__attachments">
                        <h2>Downloads</h2>
                        <ul>
                            {attachments.map((f) => (
                                <li key={f.id}>
                                    <a href={f.file_url} target="_blank" rel="noreferrer" download={f.file_name}>
                                        {f.file_name}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}
            </article>
            <Footer/>
        </div>
    );
};

export default Separate;
